"use client";

import { Shield, Search, Scale, Target, Heart, Gauge, Flame, TrendingUp, ArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";
import type { TrainingProgressCardData } from "../../types/insurabot-cards";

const S = "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

const DIMS = [
  { key: "objectionHandling" as const, label: "Objections", icon: Shield, color: "#7c3aed" },
  { key: "discovery" as const, label: "Discovery", icon: Search, color: "#0891b2" },
  { key: "compliance" as const, label: "Compliance", icon: Scale, color: "#059669" },
  { key: "closing" as const, label: "Closing", icon: Target, color: "#d97706" },
  { key: "rapport" as const, label: "Empathy", icon: Heart, color: "#db2777" },
  { key: "talkRatio" as const, label: "Pacing", icon: Gauge, color: "#4f46e5" },
];

const TIERS: Record<string, { label: string; color: string; bg: string }> = {
  none: { label: "—", color: "#9ca3af", bg: "#f3f4f6" },
  bronze: { label: "Bronze", color: "#92400e", bg: "#fef3c7" },
  silver: { label: "Silver", color: "#4b5563", bg: "#f3f4f6" },
  gold: { label: "Gold", color: "#854d0e", bg: "#fef9c3" },
  platinum: { label: "Platinum", color: "#5b21b6", bg: "#ede9fe" },
};

function getGradeColor(score: number): string {
  if (score >= 80) return "#059669";
  if (score >= 60) return "#d97706";
  return "#dc2626";
}

export default function TrainingProgressCard({ data }: { data: TrainingProgressCardData }) {
  const router = useRouter();
  const avg = Math.round(data.avgScore);
  const gradeColor = getGradeColor(avg);
  const grade = avg >= 90 ? "S" : avg >= 80 ? "A" : avg >= 70 ? "B" : avg >= 60 ? "C" : avg >= 40 ? "D" : "F";

  return (
    <div className="mt-3 rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ boxShadow: S }}>
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between">
        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">Your Training Progress</p>
        <div className="flex items-center gap-2">
          {data.streak.current > 0 && (
            <div className="flex items-center gap-1">
              <Flame className="h-3 w-3 text-orange-500" />
              <span className="text-[10px] font-mono font-bold text-gray-700" style={{ fontFeatureSettings: '"tnum"' }}>{data.streak.current}d</span>
            </div>
          )}
          {data.weekOverWeek.delta !== 0 && (
            <div className="flex items-center gap-0.5">
              <TrendingUp className={`h-3 w-3 ${data.weekOverWeek.delta > 0 ? "text-emerald-500" : "text-red-500"}`} />
              <span className={`text-[10px] font-mono font-bold ${data.weekOverWeek.delta > 0 ? "text-emerald-600" : "text-red-500"}`} style={{ fontFeatureSettings: '"tnum"' }}>
                {data.weekOverWeek.delta > 0 ? "+" : ""}{Math.round(data.weekOverWeek.delta)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Score hero */}
      <div className="bg-gray-50 px-4 py-3 flex items-center gap-4">
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-mono font-black leading-none" style={{ color: gradeColor, fontFeatureSettings: '"tnum"', letterSpacing: "-0.04em" }}>
            {avg}
          </span>
          <span className="text-lg font-mono font-black leading-none" style={{ color: gradeColor, opacity: 0.3 }}>{grade}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span><span className="font-mono font-bold text-gray-700">{data.completedSessions}</span> sessions</span>
          <span><span className="font-mono font-bold text-gray-700">{Math.round(data.bestScore)}</span> best</span>
          <span><span className="font-mono font-bold text-gray-700">{data.totalMinutes}m</span> trained</span>
        </div>
      </div>

      {/* Dimension bars */}
      <div className="bg-white px-4 py-3 space-y-2">
        {DIMS.map(({ key, label, icon: Icon, color }) => {
          const score = Math.round(data.dimensionAverages[key]);
          const tier = TIERS[data.masteryLevels[key]] || TIERS.none;
          return (
            <div key={key} className="flex items-center gap-2">
              <Icon className="h-3 w-3 flex-shrink-0" style={{ color }} strokeWidth={1.5} />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-gray-600">{label}</span>
                    <span className="text-[7px] font-bold uppercase px-1 py-px rounded" style={{ color: tier.color, backgroundColor: tier.bg }}>{tier.label}</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-gray-900" style={{ fontFeatureSettings: '"tnum"' }}>{score}</span>
                </div>
                <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <button
        onClick={() => router.push("/training")}
        className="w-full bg-white px-4 py-2.5 flex items-center justify-center gap-1.5 text-xs font-bold text-primary hover:bg-primary/5 transition-colors border-t border-gray-100"
      >
        Go to Training
        <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}
